import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/marketing/sections/page-hero";
import { allCoursesSearchSchema } from "@/features/catalog/allCoursesSearch";
import { AllCourses, FeaturedCourseCard, PopularCourses } from "@/features/catalog/components";
import { useCatalogHighlights } from "@/features/catalog/hooks/useCatalogHighlights";

export const Route = createFileRoute("/_marketing/courses")({
  // The All courses view (search, filters, sort, page) lives in the URL so refresh, Back and shared links keep it.
  validateSearch: allCoursesSearchSchema,
  component: CoursesPage,
});

/**
 * CoursesPage - Main course catalog page
 *
 * Structure:
 * 1. Hero - "Courses" title and lead, with the featured course beside them
 * 2. Most popular - Ranked course row (renders nothing when empty, or until it loads)
 * 3. All courses - Course catalogue
 *
 * The marketing layout provides the header, `<main>` and footer.
 */
function CoursesPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Nothing renders until the highlights load, so a learner who completed the featured course never sees it flash.
  const { data: highlights } = useCatalogHighlights();
  const featuredCourse = highlights?.featuredCourse;
  const popularCourses = highlights?.popularCourses ?? [];

  return (
    <>
      <PageHero
        title="Courses"
        lead="Online courses for professionals in health, social care and education, and for parents and carers. Learn at your own pace and get a certificate when you finish."
        media={featuredCourse && <FeaturedCourseCard course={featuredCourse} />}
      />
      {popularCourses.length > 0 && (
        <div className="mx-auto max-w-7xl px-6 py-8 md:px-8 md:py-12">
          <PopularCourses courses={popularCourses} />
        </div>
      )}
      <AllCoursesSection />
    </>
  );
}

/**
 * AllCoursesSection - The course catalogue table, its view kept in the URL search params.
 */
function AllCoursesSection() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <section
      className="bg-arc-surface py-section-sm md:py-section"
      aria-labelledby="all-courses-heading"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 md:px-8">
        {/* Section Header */}
        <div className="flex flex-col gap-2">
          <p className="text-eyebrow text-arc-primary-900 font-body">Course catalogue</p>
          <h2
            id="all-courses-heading"
            className="text-arc-gray-900 font-display text-display-sm md:text-display-md font-semibold"
          >
            All courses
          </h2>
          <p className="text-arc-gray-600 text-lead">
            Search by topic, or filter by level, who the course is for and price.
          </p>
        </div>

        <AllCourses
          search={search}
          onSearchChange={(next, options) =>
            navigate({ search: next, replace: options?.replace, resetScroll: false })
          }
        />
      </div>
    </section>
  );
}
